import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, uploadFile } from '../../core/api.js';
import { useAuth } from '../../core/auth.js';
import { rememberAvatarPreview } from '../../components/avatar-preview.js';

/** Choix et retrait de la photo de profil, partagés par Profil et Mon compte. */
export function useProfilePhoto(): {
  busy: boolean;
  error: string | undefined;
  save: (file: File) => Promise<void>;
  remove: () => Promise<void>;
} {
  const { reload } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const run = async (task: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
    try {
      await task();
      await reload();
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['personnel'] });
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return {
    busy,
    error,
    save: async (file) => {
      if (file.size > 5 * 1024 * 1024) {
        setError('La photo ne doit pas dépasser 5 Mo.');
        return;
      }
      await run(async () => {
        const uploaded = await uploadFile(file, 'avatar');
        // L'image choisie s'affiche IMMÉDIATEMENT, le temps que l'analyse
        // antivirus rende son verdict : sans cela l'interface reste sur le
        // visuel par défaut et l'envoi passe pour un échec.
        rememberAvatarPreview(uploaded.fileId, file);
        await api('/users/me/avatar', { method: 'PATCH', body: { fileId: uploaded.fileId } });
      });
    },
    remove: () => run(() => api('/users/me/avatar', { method: 'PATCH', body: { fileId: null } })),
  };
}
